import { ProgressBar } from './progress-bar';

export type UploadRowStatus = 'uploading' | 'done' | 'failed' | 'rejected';

export interface UploadRowProps {
  name: string;
  /** "48 MB". Shown with the percentage while uploading. */
  sizeLabel?: string;
  status: UploadRowStatus;
  /** 0-1 while uploading. */
  progress?: number;
  /** Why a file was refused ("380 MB is too big. Max 250 MB."). A failed upload says "Upload failed" on its own. */
  message?: string;
  /** Stops an upload in flight. */
  onCancel?: () => void;
  /** Takes the row away: after a failure, a refusal, or a finished upload that isn't wanted. */
  onRemove?: () => void;
  onRetry?: () => void;
}

const ACTION =
  'shrink-0 rounded-(--mbm-radius-xs) text-label-medium text-text-link focus-visible:outline-none focus-visible:shadow-(--mbm-shadow-focus-ring)';

/**
 * One file in an upload list (Figma P01 "Uploads"): its name, then the way out (Cancel while it uploads, Remove
 * otherwise), then a line that says how it is going: a progress bar, "Upload failed · Retry", or why it was refused.
 */
export function UploadRow({
  name,
  sizeLabel,
  status,
  progress = 0,
  message,
  onCancel,
  onRemove,
  onRetry,
}: UploadRowProps) {
  const percent = Math.round(progress * 100);
  const uploading = status === 'uploading';
  const way = uploading ? onCancel : onRemove;

  return (
    <li className="flex flex-col gap-(--mbm-space-1-5) rounded-(--mbm-radius-sm) bg-surface-tint p-(--mbm-space-3)">
      <div className="flex items-center justify-between gap-(--mbm-space-3)">
        <span className="min-w-0 truncate text-label-medium text-text-primary">{name}</span>
        {way ? (
          <button type="button" onClick={way} className={ACTION}>
            {uploading ? 'Cancel' : 'Remove'}
          </button>
        ) : null}
      </div>
      {uploading ? (
        <>
          <span className="text-body-xsmall text-text-secondary">
            {[sizeLabel, `${percent}%`].filter(Boolean).join(' · ')}
          </span>
          <ProgressBar value={percent} track="strong" label={`Uploading ${name}`} />
        </>
      ) : null}
      {status === 'failed' ? (
        <span role="alert" className="text-body-xsmall text-feedback-error">
          {message ?? 'Upload failed'}
          {onRetry ? (
            <>
              {' · '}
              <button
                type="button"
                onClick={onRetry}
                className="rounded-(--mbm-radius-xs) focus-visible:outline-none focus-visible:shadow-(--mbm-shadow-focus-ring)"
              >
                Retry
              </button>
            </>
          ) : null}
        </span>
      ) : null}
      {status === 'rejected' ? (
        <span role="alert" className="text-body-xsmall text-feedback-error">
          {message}
        </span>
      ) : null}
    </li>
  );
}
